import { normalizePhone, telHref, whatsappHref, formatPhoneDisplay } from '@/lib/phone';

/**
 * companyConfig — SOURCE DE VÉRITÉ UNIQUE de l'identité juridique et des
 * coordonnées de MTB BTP.
 *
 * Règles permanentes :
 * - La structure juridique est « MTB GROUP SARLU ».
 * - « MTB Global Group » n'est employé que comme marque commerciale.
 * - Aucun identifiant administratif (RCCM, ID.NAT, NIF, CNSS) n'est inventé :
 *   ils restent `null` jusqu'à validation documentaire officielle.
 * - Un numéro secondaire n'est publié que si `verified === true`.
 * - Le numéro AFAMEKIN2 (+243 898 333 056) appartient au partenaire et n'est
 *   jamais déclaré comme numéro MTB BTP ni comme « TollFree ».
 */

export type PhoneEntry = {
  /** Numéro brut (E.164 attendu). */
  raw: string;
  /** Publiable uniquement si vrai. */
  verified: boolean;
  label: { fr: string; en: string };
};

const assertPhone = (raw: string): string => {
  const e164 = normalizePhone(raw);
  if (!e164) throw new Error(`[companyConfig] Numéro invalide : ${raw}`);
  return e164;
};

/** WhatsApp principal ET ligne principale publiée. */
const PRIMARY_RAW = '+243896416208';
/** Ancienne ligne d'appel — non publiée tant qu'elle n'est pas validée. */
const SECONDARY_RAW = '+243819919510';

const primary: PhoneEntry = {
  raw: assertPhone(PRIMARY_RAW),
  verified: true,
  label: { fr: 'Téléphone & WhatsApp', en: 'Phone & WhatsApp' },
};

const secondary: PhoneEntry = {
  raw: assertPhone(SECONDARY_RAW),
  verified: false,
  label: { fr: 'Ligne secondaire', en: 'Secondary line' },
};

export const companyConfig = {
  commercialName: 'MTB BTP',
  legalName: 'MTB BTP — pôle Bâtiments & Travaux Publics de MTB GROUP SARLU',
  /** Entité juridique de rattachement. */
  legalEntity: 'MTB GROUP SARLU',
  /** Marque commerciale uniquement (jamais utilisée comme entité légale). */
  commercialBrand: 'MTB Global Group',
  tagline: 'Construire durablement. Livrer avec rigueur.',
  officialEmail: 'contact@mtbbtp.com',
  primaryPhone: primary,
  whatsappNumber: primary,
  secondaryPhone: secondary,
  address: {
    /** Passer à true uniquement après validation documentaire officielle. */
    validated: true,
    streetAddress: 'N°62, Avenue Kikwit, Quartier Pinzi',
    locality: 'Commune de Kalamu, Kinshasa',
    region: 'Kinshasa',
    countryCode: 'CD',
    country: { fr: 'République Démocratique du Congo', en: 'Democratic Republic of the Congo' },
  },
  website: 'https://www.mtbbtp.com',
  socialLinks: {
    facebook: 'https://www.facebook.com/share/17xEzMXDRG/?mibextid=wwXIfr',
    linkedin: 'https://www.linkedin.com/company/mtb-btp/',
    group: 'https://www.mtbglobalgroup.com',
  },
  /** Jamais inventés — publiés seulement quand les documents sont validés. */
  legalIdentifiers: {
    rccm: null as string | null,
    idNat: null as string | null,
    nif: null as string | null,
    cnss: null as string | null,
  },
} as const;

/** Numéros réellement publiables (verified === true). */
export const publishedPhones: PhoneEntry[] = [companyConfig.primaryPhone, companyConfig.secondaryPhone].filter(
  (p) => p.verified,
);

export const PRIMARY_PHONE_E164 = companyConfig.primaryPhone.raw;
export const PRIMARY_PHONE_DISPLAY = formatPhoneDisplay(companyConfig.primaryPhone.raw);
export const PRIMARY_PHONE_LINK = telHref(companyConfig.primaryPhone.raw) as string;
export const PRIMARY_WHATSAPP_LINK = whatsappHref(companyConfig.whatsappNumber.raw) as string;

/**
 * Adresse affichable : tant que `validated` est faux, seule la ville et le
 * pays sont exposés (aucune adresse non vérifiée en production ni en JSON-LD).
 */
export const addressDisplay = (lang: 'fr' | 'en' = 'fr'): string => {
  const a = companyConfig.address;
  const country = a.country[lang];
  if (!a.validated) return `Kinshasa — ${country}`;
  return `${a.streetAddress}, ${a.locality} — ${country}`;
};

/** PostalAddress schema.org, réduit à la ville si l'adresse n'est pas validée. */
export const postalAddressSchema = () => {
  const a = companyConfig.address;
  return a.validated
    ? {
        '@type': 'PostalAddress',
        streetAddress: a.streetAddress,
        addressLocality: a.locality,
        addressRegion: a.region,
        addressCountry: a.countryCode,
      }
    : {
        '@type': 'PostalAddress',
        addressLocality: 'Kinshasa',
        addressCountry: a.countryCode,
      };
};

export default companyConfig;
